import { Box } from '@mantine/core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { BoggleGameModeDefinition, BoggleGameModeId } from '~/boggle/gameModes'
import { evaluateRunSummaryScore } from '~/boggle/config/runSummaryScore'
import { evaluateBoggleRunBadges } from '~/boggle/lib/badges/evaluateRunBadges'
import { GamePageHeader, StandardBestScoreHeadingRow } from '~/boggle/ui/chrome/AppLayout'
import { RunSurface } from '~/boggle/ui/run/RunSurface'
import { RunVariantShell } from '~/boggle/ui/run/RunVariantShell'
import { useBoggleScoreFeedback } from '~/boggle/ui/run/ScoreFeedback'
import {
  buildRunSummaryAchievementEvents,
  type RunSummaryAchievementEvent,
} from '~/boggle/lib/achievements/runSummaryAchievementEvents'
import {
  createBoggleCompletedRunRecord,
  type BoggleRunAttemptIdentity,
} from '~/boggle/progress/runCompletion'
import { getBoggleProgressRepository } from '~/boggle/progress/webProgressRepository'
import { useBoggleUiState, type BoggleStore } from '~/boggle/ui/state/Store'
import {
  captureBoggleException,
  captureBoggleRunAbandoned,
  captureBoggleRunCompleted,
} from '~/boggle/telemetry/boggleTelemetry'

/**
 * Standard run variant. Owns best-score storage I/O, new-best/tie outcome,
 * and the top-of-screen Best Score badge (replacing the route-shell header).
 */
export function StandardRunSurface({
  accentColor,
  gameModeId,
  gameRulesDefinition,
  onExitRun,
  onNewGame,
  onRestartSeed,
  runAttempt,
  store,
  telemetryEnabled,
}: {
  accentColor?: string
  gameModeId: BoggleGameModeId
  gameRulesDefinition?: BoggleGameModeDefinition
  /** Leaves the run entirely (e.g. back to landing / catalog). */
  onExitRun: () => void
  onNewGame: () => void
  onRestartSeed?: () => void
  runAttempt: BoggleRunAttemptIdentity
  store: BoggleStore
  telemetryEnabled: boolean
}) {
  const state = useBoggleUiState(store)
  const scoreFeedback = useBoggleScoreFeedback(store)
  const handleRestartBase = onRestartSeed ?? onNewGame

  const [bestScore, setBestScore] = useState<null | number>(null)
  const [achievementEvents, setAchievementEvents] = useState<
    readonly RunSummaryAchievementEvent[] | null
  >(null)
  /** Best score captured at the start of the current play attempt; used for new-best/tie detection. */
  const bestBeforeRunRef = useRef<null | number>(null)

  const refreshDisplayedBestScore = useCallback(() => {
    void getBoggleProgressRepository()
      .getStandardBestScore(gameModeId)
      .then(setBestScore)
      .catch(() => setBestScore(null))
  }, [gameModeId])

  useEffect(() => {
    refreshDisplayedBestScore()
  }, [refreshDisplayedBestScore])

  useEffect(() => {
    if (state.phase !== 'playing') {
      return
    }

    let cancelled = false
    void getBoggleProgressRepository()
      .getStandardBestScore(gameModeId)
      .then(best => {
        if (!cancelled) {
          bestBeforeRunRef.current = best
        }
      })
      .catch(() => {
        if (!cancelled) {
          bestBeforeRunRef.current = null
        }
      })

    return () => {
      cancelled = true
    }
  }, [gameModeId, state.phase, state.seed])

  const earnedBadges = useMemo(
    () => evaluateBoggleRunBadges(state.runLedger),
    [state.runLedger],
  )
  const summaryScore = useMemo(
    () =>
      evaluateRunSummaryScore({
        earnedBadges,
        goldTilesUsed: state.goldTilesUsed,
        scoredWords: state.scoredWords,
        tilesCleared: state.tilesCleared,
      }),
    [earnedBadges, state.goldTilesUsed, state.scoredWords, state.tilesCleared],
  )
  const runTotal = state.phase === 'ended' ? summaryScore.total : null
  const telemetryState = useMemo(
    () => ({
      cols: state.cols,
      endReason: state.endReason,
      phase: state.phase,
      runLedger: state.runLedger,
      selection: state.selection,
      tilesCleared: state.tilesCleared,
      timeMs: state.timeMs,
    }),
    [
      state.cols,
      state.endReason,
      state.phase,
      state.runLedger,
      state.selection,
      state.tilesCleared,
      state.timeMs,
    ],
  )
  const abandonedRunSignatureRef = useRef<string | null>(null)

  const captureAbandonedRun = useCallback(
    (reason: 'exit' | 'restart') => {
      if (!telemetryEnabled || telemetryState.phase !== 'playing') {
        return
      }

      const signature = runAttempt.id
      if (abandonedRunSignatureRef.current === signature) {
        return
      }

      abandonedRunSignatureRef.current = signature
      captureBoggleRunAbandoned({
        gameModeId,
        reason,
        runAttempt,
        runKind: 'standard',
        state: telemetryState,
      })
    },
    [gameModeId, runAttempt, telemetryEnabled, telemetryState],
  )

  const handleExitRun = useCallback(() => {
    captureAbandonedRun('exit')
    refreshDisplayedBestScore()
    onExitRun()
  }, [captureAbandonedRun, onExitRun, refreshDisplayedBestScore])

  const handleNewGame = useCallback(() => {
    refreshDisplayedBestScore()
    onNewGame()
  }, [onNewGame, refreshDisplayedBestScore])

  const handleRestart = useCallback(() => {
    captureAbandonedRun('restart')
    handleRestartBase()
  }, [captureAbandonedRun, handleRestartBase])

  /**
   * Badge progress must commit when the run ends, not when the results
   * choreography finishes — skipping the score sequence should never lose an
   * unlock. We key the commit on `phase === 'ended'` so it fires exactly once
   * per run. The run attempt id is born when the attempt starts, so equal
   * seeds do not collapse distinct plays into one completion.
   */
  const committedRunSignatureRef = useRef<string | null>(null)
  useEffect(() => {
    if (telemetryState.phase !== 'ended') {
      committedRunSignatureRef.current = null
      if (telemetryState.phase === 'playing') {
        abandonedRunSignatureRef.current = null
      }
      setAchievementEvents(null)
      return
    }
    const signature = runAttempt.id
    if (committedRunSignatureRef.current === signature) {
      return
    }
    committedRunSignatureRef.current = signature
    const completedRun = createBoggleCompletedRunRecord({
      attempt: runAttempt,
      completedAt: new Date().toISOString(),
      earnedBadges,
      gameModeId,
      runLedger: telemetryState.runLedger,
      runKind: 'standard',
      summaryScore,
      submittedWords: state.scoredWords,
    })
    if (completedRun) {
      void getBoggleProgressRepository()
        .commitCompletedRun(completedRun)
        .then(result => {
          if (committedRunSignatureRef.current !== signature) {
            return
          }
          if (telemetryEnabled) {
            captureBoggleRunCompleted({
              gameModeId,
              progressSummary: result.inserted ? result.progressSummary : undefined,
              runAttempt,
              runKind: 'standard',
              state: telemetryState,
              summaryScore,
            })
          }
          setAchievementEvents(
            result.inserted
              ? buildRunSummaryAchievementEvents({
                  newlyRevealedRatingIds: result.newlyRevealedRatingIds,
                  newlyUnlockedTierIds: result.newlyUnlockedTierIds,
                })
              : [],
          )
        })
        .catch(error => {
          console.error('[boggle] failed to persist completed standard run', error)
          captureBoggleException(error, {
            gameModeId,
            operation: 'commit_completed_standard_run',
            runId: runAttempt.id,
            runKind: 'standard',
          })
          if (committedRunSignatureRef.current === signature) {
            setAchievementEvents([])
          }
        })
    } else {
      setAchievementEvents([])
    }
  }, [
    earnedBadges,
    gameModeId,
    runAttempt,
    state.scoredWords,
    summaryScore,
    telemetryEnabled,
    telemetryState,
  ])

  const standardSummaryOutcome: null | 'new-best' | 'tie' = useMemo(() => {
    if (state.phase !== 'ended' || runTotal === null) {
      return null
    }
    const previousBest = bestBeforeRunRef.current

    if (previousBest === null && runTotal > 0) {
      return 'new-best'
    }
    if (previousBest !== null && runTotal > previousBest) {
      return 'new-best'
    }
    if (previousBest !== null && runTotal === previousBest) {
      return 'tie'
    }
    return null
  }, [runTotal, state.phase])

  return (
    <RunVariantShell
      scoreFeedback={scoreFeedback}
      state={state}
      store={store}
      summaryStandardOutcome={standardSummaryOutcome}
    >
      <RunSurface
        accentColor={accentColor}
        gameRulesDefinition={gameRulesDefinition}
        onRestart={handleRestart}
        pauseConfig={{ onEndRun: handleExitRun }}
        scoreFeedback={scoreFeedback}
        state={state}
        store={store}
        summary={{
          achievementEvents,
          onBack: handleExitRun,
          onNewGame: handleNewGame,
          standardSummaryOutcome,
          summaryScore,
        }}
        topChrome={
          <GamePageHeader
            content={
              <Box
                px={16}
                pt={20}
                pb={10}
                pos="relative"
                top={-20}
                mt={4}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 10,
                }}
              >
                <StandardBestScoreHeadingRow bestScore={bestScore} />
              </Box>
            }
          />
        }
      />
    </RunVariantShell>
  )
}
